import {ChangeDetectionStrategy, Component, inject, input} from '@angular/core';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {catchError, distinctUntilChanged, map, of, switchMap} from 'rxjs';
import * as _ from 'lodash';
import {TimekeepingHttpService} from '../../services/timekeeping-http.service';
import {TimeCardHistoryChange, TimeCardHistoryItem} from '../../models/timecard-history.model';

interface TimecardStatusTooltipViewModel {
  hasStatusUpdate: boolean;
  updatedBy?: string;
  updatedAt?: number;
}

@Component({
  standalone: false,
  selector: 'timecard-status-tooltip',
  templateUrl: './timecard-status-tooltip.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TimecardStatusTooltipComponent {

  readonly timeCardIds = input<string[]>([]);

  private readonly timekeepingHttpService = inject(TimekeepingHttpService);

  /** Undefined while the history is loading. */
  readonly statusUpdate = toSignal(toObservable(this.timeCardIds).pipe(
    // Callers may pass a new array with the same ids on every check, so only real changes refetch.
    distinctUntilChanged(_.isEqual),
    switchMap((timeCardIds) => {
      if (_.isEmpty(timeCardIds)) {
        return of<TimecardStatusTooltipViewModel>({hasStatusUpdate: false});
      }
      return this.timekeepingHttpService.getTimeCardHistory(timeCardIds, 0, _.size(timeCardIds)).pipe(
        map((response) => getLatestStatusUpdate(response?.content)),
        catchError(() => of<TimecardStatusTooltipViewModel>({hasStatusUpdate: false}))
      );
    })
  ));
}

function getLatestStatusUpdate(histories: TimeCardHistoryItem[]): TimecardStatusTooltipViewModel {
  const statusChanges: TimeCardHistoryChange<string>[] = _.flatMap(histories, (history) => history?.status?.changes || []);
  const latestChange = _.maxBy(statusChanges, 'updatedAt');
  if (!latestChange) {
    return {hasStatusUpdate: false};
  }
  return {
    hasStatusUpdate: true,
    updatedBy: latestChange.updatedBy,
    updatedAt: latestChange.updatedAt
  };
}
